import { useEffect, useState } from 'react';
import {
  Download,
  FileText,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Loader2,
  ExternalLink,
  Info,
} from 'lucide-react';
import { useTaxStore } from '@/store/taxStore';
import { api } from '@/lib/api';
import { formatCurrency, formatPercent, cn } from '@/lib/utils';

// Info tooltip component
function InfoTooltip({ text }: { text: string }) {
  const [show, setShow] = useState(false);

  return (
    <span className="relative inline-block ml-1">
      <button
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        onClick={() => setShow(!show)}
        className="text-muted-foreground hover:text-foreground transition-colors"
      >
        <Info className="h-3.5 w-3.5" />
      </button>
      {show && (
        <span className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 text-xs bg-popover border border-border rounded-lg shadow-lg w-52 text-center block">
          {text}
          <span className="absolute top-full left-1/2 -translate-x-1/2 -mt-px border-4 border-transparent border-t-border" />
        </span>
      )}
    </span>
  );
}

export function TaxSummaryPage() {
  const { selectedYear, taxSummary, fetchTaxSummary, isLoading } = useTaxStore();
  const [exporting, setExporting] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [exportSuccess, setExportSuccess] = useState(false);

  useEffect(() => {
    fetchTaxSummary(selectedYear);
  }, [selectedYear, fetchTaxSummary]);

  const handleExport = async (format: string) => {
    setExporting(true);
    setExportError(null);
    setExportSuccess(false);
    try {
      const result = await api.exportTaxData(selectedYear, format);
      // For JSON, create a downloadable file
      if (format === 'json' || format === 'taxact') {
        const blob = new Blob([JSON.stringify(result, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `tax-export-${selectedYear}-${format}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        setExportSuccess(true);
        setTimeout(() => setExportSuccess(false), 3000);
      }
    } catch (err: any) {
      console.error('Export error:', err);
      setExportError(err.message || 'Failed to export. Please try again.');
      setTimeout(() => setExportError(null), 5000);
    }
    setExporting(false);
  };

  const handleExportExcel = async () => {
    setExportingExcel(true);
    setExportError(null);
    try {
      await api.exportExcel(selectedYear);
      setExportSuccess(true);
      setTimeout(() => setExportSuccess(false), 3000);
    } catch (err: any) {
      console.error('Excel export error:', err);
      setExportError(err.message || 'Failed to export Excel. Please try again.');
      setTimeout(() => setExportError(null), 5000);
    }
    setExportingExcel(false);
  };

  if (isLoading || !taxSummary) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Tax Summary</h2>
          <p className="text-muted-foreground">Complete overview for {selectedYear}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleExportExcel}
            disabled={exportingExcel}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-smooth disabled:opacity-50"
          >
            {exportingExcel ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            Export Excel
          </button>
          <button
            onClick={() => handleExport('json')}
            disabled={exporting}
            className="flex items-center gap-2 px-4 py-2 bg-muted text-foreground rounded-lg font-medium hover:bg-muted/80 transition-smooth"
          >
            <Download className="h-4 w-4" />
            Export JSON
          </button>
        </div>
      </div>

      {/* Export feedback */}
      {exportError && (
        <div className="bg-red-500/10 border border-red-500/20 text-red-500 rounded-lg p-4">
          {exportError}
        </div>
      )}
      {exportSuccess && (
        <div className="bg-green-500/10 border border-green-500/20 text-green-500 rounded-lg p-4">
          Export downloaded successfully! Check your downloads folder.
        </div>
      )}

      {/* Refund/Owed Banner */}
      <div
        className={cn(
          'rounded-xl p-6',
          taxSummary.summary?.isRefund
            ? 'bg-green-500/10 border border-green-500/20'
            : 'bg-yellow-500/10 border border-yellow-500/20'
        )}
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground mb-1">
              {taxSummary.summary?.isRefund ? 'Estimated Federal Refund' : 'Estimated Amount Owed'}
            </p>
            <p className="text-4xl font-bold">
              {formatCurrency(Math.abs(taxSummary.refundOrOwed))}
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-muted-foreground flex items-center justify-end">
              Effective Tax Rate
              <InfoTooltip text="Your actual tax rate: total tax divided by total income. Usually lower than marginal rate due to progressive tax brackets." />
            </p>
            <p className="text-2xl font-bold">{taxSummary.summary?.effectiveTaxRate}%</p>
          </div>
        </div>
      </div>

      {/* Income Section */}
      <div className="bg-card border border-border rounded-xl p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-green-500" />
          Income
        </h3>
        <div className="space-y-3">
          {[
            { label: 'Wages (W-2)', value: taxSummary.income?.wages },
            { label: 'Self-Employment', value: taxSummary.income?.selfEmployment },
            { label: 'Dividends', value: taxSummary.income?.dividends },
            { label: 'Capital Gains', value: taxSummary.income?.capitalGains },
            { label: 'Crypto Income', value: taxSummary.income?.cryptoIncome },
          ].map((item) => (
            <div key={item.label} className="flex justify-between py-2 border-b border-border last:border-0">
              <span className="text-muted-foreground">{item.label}</span>
              <span className="font-medium">{formatCurrency(item.value || 0)}</span>
            </div>
          ))}
          <div className="flex justify-between py-2 pt-4 border-t-2 border-border">
            <span className="font-semibold">Total Income</span>
            <span className="font-bold text-lg">{formatCurrency(taxSummary.income?.total || 0)}</span>
          </div>
        </div>
      </div>

      {/* Adjustments Section */}
      <div className="bg-card border border-border rounded-xl p-6">
        <h3 className="text-lg font-semibold mb-4">Adjustments to Income</h3>
        <div className="space-y-3">
          {[
            { label: 'Self-Employment Tax Deduction', value: taxSummary.adjustments?.selfEmploymentTaxDeduction },
            { label: '401(k) Contributions', value: taxSummary.adjustments?.retirement401k },
          ].map((item) => (
            <div key={item.label} className="flex justify-between py-2 border-b border-border last:border-0">
              <span className="text-muted-foreground">{item.label}</span>
              <span className="font-medium text-green-500">-{formatCurrency(item.value || 0)}</span>
            </div>
          ))}
          <div className="flex justify-between py-2 pt-4 border-t-2 border-border">
            <span className="font-semibold flex items-center">
              Adjusted Gross Income (AGI)
              <InfoTooltip text="Total income minus specific adjustments like retirement contributions and self-employment tax. Used to determine eligibility for many deductions." />
            </span>
            <span className="font-bold text-lg">{formatCurrency(taxSummary.agi || 0)}</span>
          </div>
        </div>
      </div>

      {/* Deductions Section */}
      <div className="bg-card border border-border rounded-xl p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <TrendingDown className="h-5 w-5 text-blue-500" />
          Deductions
        </h3>
        <div className="space-y-3">
          <div className="flex justify-between py-2 border-b border-border">
            <span className="text-muted-foreground">
              {taxSummary.deductions?.used === 'itemized' ? 'Itemized Deductions' : 'Standard Deduction'}
            </span>
            <span className="font-medium">{formatCurrency(taxSummary.deductions?.amount || 0)}</span>
          </div>
          {taxSummary.deductions?.qbi > 0 && (
            <div className="flex justify-between py-2 border-b border-border">
              <span className="text-muted-foreground flex items-center">
                QBI Deduction (20%)
                <InfoTooltip text="Qualified Business Income deduction: up to 20% of self-employment income can be deducted, subject to income limits." />
              </span>
              <span className="font-medium">{formatCurrency(taxSummary.deductions?.qbi || 0)}</span>
            </div>
          )}
          <div className="flex justify-between py-2 pt-4 border-t-2 border-border">
            <span className="font-semibold flex items-center">
              Taxable Income
              <InfoTooltip text="AGI minus deductions. This is the amount your income tax is calculated on." />
            </span>
            <span className="font-bold text-lg">{formatCurrency(taxSummary.taxableIncome || 0)}</span>
          </div>
        </div>
      </div>

      {/* Tax Calculation */}
      <div className="bg-card border border-border rounded-xl p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <DollarSign className="h-5 w-5 text-primary" />
          Tax Calculation
        </h3>
        <div className="space-y-3">
          <div className="flex justify-between py-2 border-b border-border">
            <span className="text-muted-foreground">Income Tax</span>
            <span className="font-medium">{formatCurrency(taxSummary.tax?.income || 0)}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-border">
            <span className="text-muted-foreground flex items-center">
              Self-Employment Tax
              <InfoTooltip text="15.3% tax on self-employment income covering Social Security (12.4%) and Medicare (2.9%). Half is deductible from income." />
            </span>
            <span className="font-medium">{formatCurrency(taxSummary.tax?.selfEmployment || 0)}</span>
          </div>
          <div className="flex justify-between py-2 pt-4 border-t-2 border-border">
            <span className="font-semibold">Total Tax</span>
            <span className="font-bold text-lg">{formatCurrency(taxSummary.tax?.total || 0)}</span>
          </div>
        </div>
      </div>

      {/* Withholdings */}
      <div className="bg-card border border-border rounded-xl p-6">
        <h3 className="text-lg font-semibold mb-4">Payments & Withholdings</h3>
        <div className="space-y-3">
          <div className="flex justify-between py-2 border-b border-border">
            <span className="text-muted-foreground">Federal Withholding</span>
            <span className="font-medium text-green-500">{formatCurrency(taxSummary.withholdings?.federal || 0)}</span>
          </div>
          <div className="flex justify-between py-2 border-b border-border">
            <span className="text-muted-foreground">Estimated Payments</span>
            <span className="font-medium text-green-500">{formatCurrency(taxSummary.withholdings?.estimated || 0)}</span>
          </div>
          <div className="flex justify-between py-2 pt-4 border-t-2 border-border">
            <span className="font-semibold">Total Payments</span>
            <span className="font-bold text-lg text-green-500">{formatCurrency(taxSummary.withholdings?.total || 0)}</span>
          </div>
        </div>
      </div>

      {/* Filing Info */}
      <div className="bg-card border border-border rounded-xl p-6">
        <h3 className="text-lg font-semibold mb-4">Filing Information</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-sm text-muted-foreground">Filing Status</p>
            <p className="font-medium capitalize">{taxSummary.filingStatus?.replace(/_/g, ' ')}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">W-2 Forms</p>
            <p className="font-medium">{taxSummary.breakdown?.w2Count || 0}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Businesses</p>
            <p className="font-medium">{taxSummary.breakdown?.businessCount || 0}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground flex items-center">
              Marginal Rate
              <InfoTooltip text="Tax rate on your last dollar of income. Higher income pushes you into higher brackets, but only the income in each bracket is taxed at that rate." />
            </p>
            <p className="font-medium">{taxSummary.summary?.marginalTaxRate}%</p>
          </div>
        </div>
      </div>

      {/* Filing Options */}
      <div className="bg-card border border-border rounded-xl p-6">
        <h3 className="text-lg font-semibold mb-4">Ready to File?</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            onClick={() => handleExport('taxact')}
            disabled={exporting}
            className="flex items-center justify-between p-4 bg-primary/10 rounded-lg hover:bg-primary/20 transition-smooth disabled:opacity-50 w-full text-left"
          >
            <div>
              <p className="font-medium">Export for TaxAct</p>
              <p className="text-sm text-muted-foreground">Download data to import into TaxAct</p>
            </div>
            {exporting ? (
              <Loader2 className="h-5 w-5 text-primary animate-spin" />
            ) : (
              <FileText className="h-5 w-5 text-primary" />
            )}
          </button>
          <a
            href="https://www.irs.gov/filing/free-file-do-your-federal-taxes-for-free"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between p-4 bg-muted/30 rounded-lg hover:bg-muted/50 transition-smooth"
          >
            <div>
              <p className="font-medium">IRS Free File</p>
              <p className="text-sm text-muted-foreground">File directly with the IRS</p>
            </div>
            <ExternalLink className="h-5 w-5 text-muted-foreground" />
          </a>
        </div>
      </div>
    </div>
  );
}
